import { Home, Hammer, CloudLightning, Wrench } from "lucide-react";

const HOUSE_IMG = "/generated/midwest_roofing_house.png";

const SERVICES = [
  { label: "Roof Repair", Icon: Wrench },
  { label: "Roof Replacement", Icon: Home },
  { label: "Storm Damage", Icon: CloudLightning },
  { label: "Gutter Services", Icon: Hammer },
];

/**
 * Fictional demo of a "Midwest Roofing" website shown inside a laptop frame
 * with an overlapping phone frame. Uses an AI-generated house photo.
 */
export const HeroMockup = () => {
  return (
    <div
      className="relative w-full max-w-[640px] mx-auto"
      data-testid="hero-mockup"
      role="img"
      aria-label="Demo website preview — Midwest Roofing is a fictional example"
    >
      {/* Laptop */}
      <div className="relative rounded-2xl bg-[#0F1B2E] p-2 shadow-2xl ring-1 ring-white/10">
        {/* Demo badge — clearly marks the mockup as fictional. */}
        <span
          className="absolute -top-2.5 left-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-[#0B1220]/90 border-l-2 border-goc-primary pl-2.5 pr-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white shadow-md ring-1 ring-white/10"
          data-testid="hero-mockup-demo-badge"
          aria-hidden="true"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-goc-primary" />
          Demo Website
        </span>
        <div className="rounded-lg bg-white overflow-hidden">
          {/* Browser chrome */}
          <div className="flex items-center gap-1.5 bg-goc-light px-3 py-2 border-b border-goc-border">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            <div className="ml-3 flex-1 text-[10px] text-goc-muted truncate">
              midwestroofing.example
            </div>
          </div>

          {/* Site content */}
          <div className="bg-white">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-goc-border">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#B91C1C] text-white text-[10px] font-bold">
                  MR
                </span>
                <span className="text-[11px] font-bold text-goc-text">
                  Midwest Roofing
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-3 text-[10px] text-goc-muted">
                <span>Services</span>
                <span>About</span>
                <span>Contact</span>
              </div>
              <span className="text-[10px] font-semibold text-white bg-[#B91C1C] px-2.5 py-1 rounded">
                Call Now
              </span>
            </div>

            <div className="relative">
              <img
                src={HOUSE_IMG}
                alt="Suburban home with a pitched shingle roof"
                loading="lazy"
                className="w-full h-40 sm:h-56 md:h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-5">
                <h3 className="text-white text-base sm:text-xl font-bold leading-tight max-w-sm">
                  Reliable Roofing You Can Trust
                </h3>
                <p className="text-white/85 text-[11px] sm:text-xs mt-1 max-w-sm">
                  Family-owned. Licensed. Serving the Midwest.
                </p>
                <button
                  type="button"
                  className="mt-3 self-start text-[11px] sm:text-xs font-semibold bg-[#B91C1C] hover:bg-[#991B1B] transition-colors text-white px-3 py-2 rounded"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  Get a Free Quote
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 p-3 sm:p-4 bg-white">
              {SERVICES.map(({ label, Icon }) => (
                <div
                  key={label}
                  className="flex flex-col items-center text-center gap-1.5 rounded-md border border-goc-border p-2"
                >
                  <Icon className="h-4 w-4 text-[#B91C1C]" strokeWidth={2.25} />
                  <span className="text-[9px] sm:text-[10px] font-medium text-goc-text leading-tight">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Laptop base */}
      <div className="mx-auto -mt-1 h-2.5 w-3/4 rounded-b-2xl bg-[#0F1B2E] shadow-inner" />
      <div className="mx-auto mt-0 h-1 w-1/2 rounded-b-lg bg-[#0B1220]/60" />

      {/* Phone */}
      <div className="hidden sm:block absolute -bottom-10 -left-6 md:-left-10 w-[140px] md:w-[160px] rounded-[22px] bg-[#0F1B2E] p-1.5 shadow-2xl ring-1 ring-white/10 rotate-[-6deg]">
        <div className="rounded-[16px] bg-white overflow-hidden">
          <div className="relative">
            <img
              src={HOUSE_IMG}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="w-full h-24 md:h-28 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute inset-0 p-2 flex flex-col justify-end">
              <span className="text-white text-[9px] font-bold leading-tight">
                Reliable Roofing
              </span>
              <span className="text-white/80 text-[7px]">Free Quote →</span>
            </div>
          </div>
          <div className="p-2 space-y-1.5">
            <div className="h-1.5 w-3/4 rounded-full bg-goc-border" />
            <div className="h-1.5 w-2/3 rounded-full bg-goc-border" />
            <div className="mt-2 h-4 rounded bg-[#B91C1C]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroMockup;
